/**
 * Completion telemetry for the IntelliSense orchestrator.
 *
 * Pure module — no I/O, no React, no Monaco. Two per-event emit functions:
 *   - `emitCompletionAccepted` when the user accepts a suggestion
 *   - `emitCompletionOrchestratorError` when a resolver/orchestrator stage throws
 * Resolutions/dismissals aggregate in `completionSessionAggregate.ts` instead
 * (see the volume note below).
 *
 * Strict payload allowlist enforced — extra keys are dropped before the
 * payload reaches the analytics IPC. Error messages are never transmitted at
 * all (analytics policy: error text is user data — no sanitizer can guarantee
 * a short bare identifier, path, or literal doesn't survive); the error event
 * carries only the categorical `engineId` + `stage`.
 *
 * Gated on `appStore.analyticsEnabled` (passed via `EmitOptions`).
 */

import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { DatabaseEngineId } from '@/core/engines';
import type { CompletionPositionKind } from '@/core/engines/intellisenseTypes';

export const COMPLETION_ACCEPTED_EVENT = 'completion.accepted';
export const COMPLETION_ORCHESTRATOR_ERROR_EVENT = 'completion.orchestrator.error';
// NOTE: per-resolution (`completion.resolved`) and per-dismissal
// (`completion.dismissed`) events were REMOVED before their 1.4.18 debut —
// quickSuggestions makes them fire per keystroke, which would fleet-scale to
// 20-100x the Umami instance's daily ingest. Resolutions and dismissals now
// aggregate locally in `completionSessionAggregate.ts` and flush as
// `completion.session_summary`. (The nightly T-SQL perf-trend gate is
// lab-harness-based and reads its own JSON artifact, not these events.)

export type CompletionDismissReason =
  | 'esc'
  | 'cursor-moved'
  | 'click-outside'
  | 'replaced'
  | 'other';

export type CompletionOrchestratorStage =
  | 'parse'
  | 'context'
  | 'resolve'
  | 'rank'
  | 'monaco-bridge';

export type CompletionAcceptedPayload = {
  readonly engineId: DatabaseEngineId;
  readonly positionKind: CompletionPositionKind;
  readonly tier: number;
  // matchQuality (ranker's prefix-match score) is intentionally absent — only `tier` via
  // sortText survives the Monaco bridge; emitting a constant corrupts the ranking-regression
  // signal. Re-add here when the deferred "Per-Item Metadata Plumbing" slice plumbs the real value.
  readonly rankIndex: number;
  readonly latencyMs: number;
};

export type CompletionOrchestratorErrorPayload = {
  readonly engineId: DatabaseEngineId;
  readonly stage: CompletionOrchestratorStage;
  // No `message` field — error text is user data and never crosses the
  // analytics boundary, even "sanitized". Stage + engine is enough to spot a
  // broken resolver stage in the wild; the full error stays in local logs.
};

/**
 * Which classifier produced `positionKind`. `'clause'` = the text clause
 * machine (`sqlClauseToCpk`); `'slot'` = the GRA-8 fine-grained slot gate
 * (`resolveSlotKind` → `slotKindToCpk`) overrode an `unknown` clause result.
 * Categorical, low-cardinality — the which-path counter the pilot's hard
 * constraint watches to prove the `'slot'` path never produces an empty
 * suggestion set where `'clause'` produced one. The future tree slice adds a
 * `'slot-tree'` value to the same field (forward-compatible).
 */
export type CompletionClassifiedBy = 'clause' | 'slot';

export type EmitOptions = {
  readonly analyticsEnabled?: boolean;
};

const ALLOWED_ACCEPTED_KEYS = [
  'engineId',
  'positionKind',
  'tier',
  // matchQuality omitted — deferred; see CompletionAcceptedPayload comment.
  'rankIndex',
  'latencyMs',
] as const;

const ALLOWED_ERROR_KEYS = [
  'engineId',
  'stage',
] as const;

export const COMPLETION_TELEMETRY_ALLOWLIST = {
  accepted: ALLOWED_ACCEPTED_KEYS,
  error: ALLOWED_ERROR_KEYS,
} as const;

function pickAllowed<K extends string>(
  payload: Record<string, unknown>,
  allowed: readonly K[],
): Record<K, string | number | boolean> {
  const out = {} as Record<K, string | number | boolean>;
  for (const key of allowed) {
    const value = payload[key];
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      out[key] = value;
    }
  }
  return out;
}

export function emitCompletionAccepted(
  track: TrackFunction,
  payload: CompletionAcceptedPayload,
  options?: EmitOptions,
): void {
  if (options?.analyticsEnabled === false) return;
  track(
    COMPLETION_ACCEPTED_EVENT,
    pickAllowed(payload as unknown as Record<string, unknown>, ALLOWED_ACCEPTED_KEYS),
  );
}

export function emitCompletionOrchestratorError(
  track: TrackFunction,
  payload: CompletionOrchestratorErrorPayload,
  options?: EmitOptions,
): void {
  if (options?.analyticsEnabled === false) return;
  track(
    COMPLETION_ORCHESTRATOR_ERROR_EVENT,
    pickAllowed(payload as unknown as Record<string, unknown>, ALLOWED_ERROR_KEYS),
  );
}

